/**
 * UI - Elegant Consent Modal
 * Minimalist, GDPR-compliant design
 */

export class UI {
  constructor(manager, config, t) {
    this.manager = manager;
    this.config = config;
    this.t = t;

    this.modal = null;
    this.settingsModal = null;
    this.floatingButton = null;
  }

  /**
   * Show consent modal (main banner as modal)
   */
  showBanner() {
    if (this.modal) return;

    this.modal = document.createElement('div');
    this.modal.className = 'consent';
    this.modal.setAttribute('role', 'dialog');
    this.modal.setAttribute('aria-modal', 'true');
    this.modal.setAttribute('aria-label', this.t.bannerTitle);

    this.modal.innerHTML = `
      <div class="consent__backdrop"></div>
      <div class="consent__card">
        <button class="consent__close" data-consent-close aria-label="Close">×</button>

        <div class="consent__icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="12" cy="12" r="10"/>
            <circle cx="8" cy="9" r="1.5" fill="currentColor"/>
            <circle cx="15" cy="10" r="1" fill="currentColor"/>
            <circle cx="10" cy="14" r="1" fill="currentColor"/>
            <circle cx="14" cy="15" r="0.75" fill="currentColor"/>
            <circle cx="7" cy="13" r="0.5" fill="currentColor"/>
          </svg>
        </div>

        <h2 class="consent__title">${this.t.bannerTitle}</h2>
        <p class="consent__text">${this.t.bannerText}</p>

        <div class="consent__actions">
          <button class="consent__btn consent__btn--reject" data-consent-reject>
            ${this.t.rejectAll}
          </button>
          <button class="consent__btn consent__btn--accept" data-consent-accept>
            ${this.t.acceptAll}
          </button>
        </div>

        <div class="consent__footer">
          <button class="consent__link" data-consent-settings>${this.t.settings}</button>
          <span class="consent__separator">|</span>
          <a href="${this.config.policyUrl}" class="consent__link" target="_blank" rel="noopener">${this.t.learnMore}</a>
        </div>
      </div>
    `;

    // Event: Accept
    this.modal.querySelector('[data-consent-accept]').addEventListener('click', () => {
      this.manager.acceptAll();
      this.hideModal();
      this.createFloatingButton();
    });

    // Event: Reject
    this.modal.querySelector('[data-consent-reject]').addEventListener('click', () => {
      this.manager.rejectAll();
      this.hideModal();
      this.createFloatingButton();
    });

    // Event: Close (X) = Reject
    this.modal.querySelector('[data-consent-close]').addEventListener('click', () => {
      this.manager.rejectAll();
      this.hideModal();
      this.createFloatingButton();
    });

    // Event: Backdrop click = Reject
    this.modal.querySelector('.consent__backdrop').addEventListener('click', () => {
      this.manager.rejectAll();
      this.hideModal();
      this.createFloatingButton();
    });

    // Event: Settings
    this.modal.querySelector('[data-consent-settings]').addEventListener('click', () => {
      this.showSettings();
    });

    // Apply primary color
    this.modal.style.setProperty('--consent-primary', this.config.primaryColor);

    document.body.appendChild(this.modal);
    document.body.style.overflow = 'hidden';

    // Animate in
    requestAnimationFrame(() => {
      this.modal.classList.add('consent--visible');
    });
  }

  /**
   * Hide consent modal
   */
  hideModal() {
    if (!this.modal) return;

    document.body.style.overflow = '';
    this.modal.classList.remove('consent--visible');

    setTimeout(() => {
      this.modal?.remove();
      this.modal = null;
    }, 400);
  }

  // Alias for compatibility
  hideBanner() {
    this.hideModal();
  }

  /**
   * Show settings modal
   */
  showSettings() {
    if (this.settingsModal) return;

    const consent = this.manager.getConsent();

    this.settingsModal = document.createElement('div');
    this.settingsModal.className = 'consent-settings';
    this.settingsModal.setAttribute('role', 'dialog');
    this.settingsModal.setAttribute('aria-modal', 'true');
    this.settingsModal.setAttribute('aria-label', this.t.settingsTitle);

    this.settingsModal.innerHTML = `
      <div class="consent-settings__backdrop" data-close></div>
      <div class="consent-settings__card">
        <button class="consent-settings__close" data-close aria-label="Close">×</button>

        <h2 class="consent-settings__title">${this.t.settingsTitle}</h2>
        <p class="consent-settings__intro">${this.t.settingsIntro}</p>

        <div class="consent-settings__categories">
          <div class="consent-settings__category">
            <div class="consent-settings__row">
              <div class="consent-settings__info">
                <span class="consent-settings__name">${this.t.necessary}</span>
                <span class="consent-settings__badge">${this.t.alwaysActive}</span>
              </div>
              <label class="consent-settings__toggle consent-settings__toggle--disabled">
                <input type="checkbox" checked disabled>
                <span class="consent-settings__slider"></span>
              </label>
            </div>
            <p class="consent-settings__desc">${this.t.necessaryDesc}</p>
          </div>

          <div class="consent-settings__category">
            <div class="consent-settings__row">
              <div class="consent-settings__info">
                <span class="consent-settings__name">${this.t.analytics}</span>
              </div>
              <label class="consent-settings__toggle">
                <input type="checkbox" ${consent.analytics ? 'checked' : ''} data-category="analytics">
                <span class="consent-settings__slider"></span>
              </label>
            </div>
            <p class="consent-settings__desc">${this.t.analyticsDesc}</p>
          </div>

          <div class="consent-settings__category">
            <div class="consent-settings__row">
              <div class="consent-settings__info">
                <span class="consent-settings__name">${this.t.marketing}</span>
              </div>
              <label class="consent-settings__toggle">
                <input type="checkbox" ${consent.marketing ? 'checked' : ''} data-category="marketing">
                <span class="consent-settings__slider"></span>
              </label>
            </div>
            <p class="consent-settings__desc">${this.t.marketingDesc}</p>
          </div>
        </div>

        <div class="consent-settings__actions">
          <button class="consent-settings__btn consent-settings__btn--save" data-save>
            ${this.t.saveSettings}
          </button>
          <button class="consent-settings__btn consent-settings__btn--accept" data-accept>
            ${this.t.acceptAll}
          </button>
        </div>
      </div>
    `;

    // Event: Close
    this.settingsModal.querySelectorAll('[data-close]').forEach(el => {
      el.addEventListener('click', () => this.hideSettings());
    });

    // Event: Save
    this.settingsModal.querySelector('[data-save]').addEventListener('click', () => {
      const choices = {
        analytics: this.settingsModal.querySelector('[data-category="analytics"]').checked,
        marketing: this.settingsModal.querySelector('[data-category="marketing"]').checked,
      };
      this.manager.saveCustom(choices);
      this.hideSettings();
      this.hideModal();
      this.createFloatingButton();
    });

    // Event: Accept All
    this.settingsModal.querySelector('[data-accept]').addEventListener('click', () => {
      this.manager.acceptAll();
      this.hideSettings();
      this.hideModal();
      this.createFloatingButton();
    });

    // Escape key
    this.settingsEscHandler = (e) => {
      if (e.key === 'Escape') this.hideSettings();
    };
    document.addEventListener('keydown', this.settingsEscHandler);

    // Apply primary color
    this.settingsModal.style.setProperty('--consent-primary', this.config.primaryColor);

    document.body.appendChild(this.settingsModal);

    // Animate in
    requestAnimationFrame(() => {
      this.settingsModal.classList.add('consent-settings--visible');
    });
  }

  /**
   * Hide settings modal
   */
  hideSettings() {
    if (!this.settingsModal) return;

    document.removeEventListener('keydown', this.settingsEscHandler);
    this.settingsModal.classList.remove('consent-settings--visible');

    setTimeout(() => {
      this.settingsModal?.remove();
      this.settingsModal = null;
    }, 400);
  }

  // Alias for showSettings
  showModal() {
    this.showSettings();
  }

  /**
   * Create floating button
   */
  createFloatingButton() {
    if (this.floatingButton) return;

    this.floatingButton = document.createElement('button');
    this.floatingButton.className = 'consent-fab';
    if (this.config.floatingPosition === 'right') {
      this.floatingButton.classList.add('consent-fab--right');
    }
    this.floatingButton.setAttribute('aria-label', this.t.settings);
    this.floatingButton.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <circle cx="12" cy="12" r="10"/>
        <circle cx="8" cy="9" r="1.5" fill="currentColor"/>
        <circle cx="15" cy="10" r="1" fill="currentColor"/>
        <circle cx="10" cy="14" r="1" fill="currentColor"/>
        <circle cx="14" cy="15" r="0.75" fill="currentColor"/>
      </svg>
    `;

    this.floatingButton.addEventListener('click', () => {
      this.showSettings();
    });

    this.floatingButton.style.setProperty('--consent-primary', this.config.primaryColor);

    // Custom bottom position
    if (this.config.fabBottom) {
      this.floatingButton.style.setProperty('--consent-fab-bottom', this.config.fabBottom);
    }
    if (this.config.fabBottomMobile) {
      this.floatingButton.style.setProperty('--consent-fab-bottom-mobile', this.config.fabBottomMobile);
    }

    document.body.appendChild(this.floatingButton);

    requestAnimationFrame(() => {
      this.floatingButton.classList.add('consent-fab--visible');
    });
  }

  /**
   * Remove floating button
   */
  removeFloatingButton() {
    if (this.floatingButton) {
      this.floatingButton.remove();
      this.floatingButton = null;
    }
  }
}
